import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ReportService } from '../core/services/report.service';
import {
  DATE_PRESET_OPTIONS,
  DatePresetFilter,
  FilterOperator,
  OPERATOR_OPTIONS,
  ReportDefinition,
  ReportFilter,
  ReportResult,
  ReportSource,
  ResolvedField,
} from '../core/models/report.model';

@Component({
  selector: 'app-reports',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <!-- ── LIST VIEW ─────────────────────────────────────────────────── -->
    <div *ngIf="view === 'list'" class="page">
      <div class="page-header">
        <div>
          <h1>Reports</h1>
          <p class="subtitle">Build and run custom queries across your Directus collections.</p>
        </div>
        <button class="btn-primary" (click)="newReport()">+ New Report</button>
      </div>

      <div class="card" *ngIf="loadingReports">
        <div class="empty-state"><p class="muted">Loading reports…</p></div>
      </div>

      <div class="card" *ngIf="!loadingReports && savedReports.length === 0">
        <div class="empty-state">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
            <path stroke-linecap="round" stroke-linejoin="round"
              d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2M9 5a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2M9 5a2 2 0 0 0 2-2h2a2 2 0 0 0 2 2" />
          </svg>
          <p>No reports yet. Create your first report to get started.</p>
          <button class="btn-primary" (click)="newReport()">+ New Report</button>
        </div>
      </div>

      <div class="card table-card" *ngIf="!loadingReports && savedReports.length > 0">
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Mode</th>
              <th>Collections</th>
              <th>Summary</th>
              <th>Updated</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let report of savedReports">
              <td class="report-name">{{ report.name }}</td>
              <td><span class="badge" [class.multi]="report.mode === 'multi'">{{ report.mode }}</span></td>
              <td class="muted">{{ collectionsOf(report) }}</td>
              <td class="muted small">{{ svc.summaryLine(report) }}</td>
              <td class="muted small">{{ report.updatedAt | date:'dd MMM yyyy' }}</td>
              <td class="actions-cell">
                <button class="btn-sm" (click)="runReport(report)">Run</button>
                <button class="btn-sm" (click)="editReport(report)">Edit</button>
                <button class="btn-sm danger" (click)="deleteReport(report.id)">Delete</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Inline result after "Run" from list ─────────────────────────── -->
      <ng-container *ngIf="listResult">
        <div class="card result-card">
          <div class="result-header">
            <span class="result-title">{{ listResultName }}</span>
            <button class="btn-sm" (click)="exportCsv(listResult!, listResultName)">Export CSV</button>
            <button class="btn-ghost" (click)="listResult = null">✕</button>
          </div>
          <ng-container *ngTemplateOutlet="vizBlock; context: { result: listResult, def: listResultDef }"></ng-container>
        </div>
      </ng-container>
    </div>

    <!-- ── BUILDER VIEW ───────────────────────────────────────────────── -->
    <div *ngIf="view === 'builder'" class="page">
      <div class="page-header">
        <div>
          <h1>{{ draft.id && isEditing ? 'Edit Report' : 'New Report' }}</h1>
        </div>
        <button class="btn-ghost" (click)="backToList()">← Back to Reports</button>
      </div>

      <div class="builder-grid">
        <!-- LEFT: Config ────────────────────────────────────────────────── -->
        <div class="builder-col">

          <!-- Name -->
          <div class="card section-card">
            <label class="field-label">Report Name</label>
            <input class="text-input" [(ngModel)]="draft.name" placeholder="e.g. Event Revenue by Month" />
            <span class="field-error" *ngIf="nameError">{{ nameError }}</span>
          </div>

          <!-- Mode toggle -->
          <div class="card section-card">
            <label class="field-label">Mode</label>
            <div class="mode-toggle">
              <button [class.active]="draft.mode === 'single'" (click)="setMode('single')">Single collection</button>
              <button [class.active]="draft.mode === 'multi'" (click)="setMode('multi')">Multi-source</button>
            </div>
            <p class="hint" *ngIf="draft.mode === 'multi'">
              Run separate queries on different collections and merge by a shared group-by dimension.
            </p>
          </div>

          <!-- Aggregation mode (single mode only) -->
          <div class="card section-card" *ngIf="draft.mode === 'single'">
            <label class="field-label">Query method</label>
            <div class="mode-toggle">
              <button [class.active]="draft.aggregationMode === 'server'" (click)="setAggregationMode('server')">
                Server aggregation
              </button>
              <button [class.active]="draft.aggregationMode === 'client'" (click)="setAggregationMode('client')">
                Leaderboard / Top N
              </button>
            </div>
            <p class="hint" *ngIf="draft.aggregationMode === 'server'">
              Fast. Directus aggregates on the server. Group-by only supports direct fields.
            </p>
            <p class="hint" *ngIf="draft.aggregationMode === 'client'">
              Fetches all rows then aggregates in the browser. Supports grouping by related fields
              (e.g. member name). Best for leaderboards and top-N rankings.
            </p>
            <ng-container *ngIf="draft.aggregationMode === 'client'">
              <div class="leaderboard-opts">
                <div class="lb-opt">
                  <label class="field-label">Show top</label>
                  <div class="topn-row">
                    <input class="text-input topn-input" type="number" min="0" [(ngModel)]="draft.topN" />
                    <span class="hint">rows &nbsp;(0 = all)</span>
                  </div>
                </div>
                <div class="lb-opt">
                  <label class="field-label">Sort order</label>
                  <div class="mode-toggle">
                    <button [class.active]="draft.sortDesc" (click)="draft.sortDesc = true">Highest first</button>
                    <button [class.active]="!draft.sortDesc" (click)="draft.sortDesc = false">Lowest first</button>
                  </div>
                </div>
              </div>
            </ng-container>
          </div>

          <!-- Group By (shared for all sources) -->
          <div class="card section-card">
            <label class="field-label">Group By <span class="required" *ngIf="draft.mode === 'multi'">*</span></label>
            <select class="select-input" [(ngModel)]="draft.groupBy" (ngModelChange)="onGroupByChange()">
              <option [ngValue]="null">None (aggregate all into a single value)</option>
              <optgroup *ngFor="let src of draft.sources; let si = index"
                [label]="draft.mode === 'multi' ? 'From: ' + (src.collection || 'Source ' + (si+1)) : 'Available dimensions'">
                <ng-container *ngFor="let opt of groupByOptionsFor(si)">
                  <option [value]="opt.value">{{ opt.label }}</option>
                </ng-container>
              </optgroup>
            </select>
          </div>

          <!-- Sources -->
          <div *ngFor="let source of draft.sources; let si = index" class="card section-card source-card">
            <div class="source-header">
              <span class="source-num" *ngIf="draft.mode === 'multi'">Source {{ si + 1 }}</span>
              <button *ngIf="draft.mode === 'multi' && draft.sources.length > 2"
                class="btn-icon danger" (click)="removeSource(si)" title="Remove source">✕</button>
            </div>

            <!-- Series label (multi only) -->
            <div *ngIf="draft.mode === 'multi'" class="field-row">
              <label class="field-label">Series Label</label>
              <input class="text-input" [(ngModel)]="source.label" placeholder="e.g. Event Revenue" />
            </div>

            <!-- Collection picker -->
            <div class="field-row">
              <label class="field-label">Collection</label>
              <div class="select-wrap">
                <select class="select-input" [(ngModel)]="source.collection"
                  (ngModelChange)="onCollectionChange(si)">
                  <option value="">— select a collection —</option>
                  <option *ngFor="let c of availableCollections" [value]="c">{{ c }}</option>
                </select>
                <span class="loading-hint" *ngIf="loadingCollections">Loading…</span>
              </div>
            </div>

            <!-- Metric -->
            <div class="field-row">
              <label class="field-label">Metric</label>
              <div class="metric-row">
                <select class="select-input short" [(ngModel)]="source.metric.type"
                  (ngModelChange)="onMetricTypeChange(source)">
                  <option value="count">Count</option>
                  <option value="sum">Sum</option>
                  <option value="avg">Average</option>
                </select>
                <span class="metric-of" *ngIf="source.metric.type !== 'count'">of</span>
                <select *ngIf="source.metric.type !== 'count'" class="select-input"
                  [(ngModel)]="source.metric.field">
                  <option value="">— select field —</option>
                  <option *ngFor="let f of numericFieldsFor(si)" [value]="f.key">{{ f.label }}</option>
                </select>
              </div>
            </div>

            <!-- Filters -->
            <div class="field-row">
              <label class="field-label">Filters</label>
              <div *ngFor="let filter of source.filters; let fi = index" class="filter-row">
                <select class="select-input" [(ngModel)]="filter.field"
                  (ngModelChange)="onFilterFieldChange(source, filter)">
                  <option value="">— field —</option>
                  <option *ngFor="let f of filterableFieldsFor(si)" [value]="f.key">{{ f.label }}</option>
                </select>
                <select class="select-input short" [(ngModel)]="filter.operator">
                  <option *ngFor="let op of operatorsFor(source, filter)" [value]="op.value">{{ op.label }}</option>
                </select>
                <input *ngIf="!isNullOperator(filter.operator)" class="text-input filter-val"
                  [(ngModel)]="filter.value" placeholder="value" />
                <button class="btn-icon danger" (click)="removeFilter(source, fi)" title="Remove filter">✕</button>
              </div>
              <button class="btn-ghost small" (click)="addFilter(source)"
                [disabled]="!source.collection">+ Add Filter</button>
            </div>

            <!-- Date presets -->
            <div class="field-row">
              <label class="field-label">Date Range</label>
              <div *ngFor="let dp of source.datePresets; let di = index" class="filter-row">
                <select class="select-input" [(ngModel)]="dp.field">
                  <option value="">— field —</option>
                  <option *ngFor="let f of datetimeFieldsFor(si)" [value]="f.key">{{ f.label }}</option>
                </select>
                <select class="select-input" [(ngModel)]="dp.preset">
                  <option *ngFor="let opt of datePresetOptions" [value]="opt.value">{{ opt.label }}</option>
                </select>
                <button class="btn-icon danger" (click)="removeDatePreset(source, di)" title="Remove">✕</button>
              </div>
              <button class="btn-ghost small" (click)="addDatePreset(source)"
                [disabled]="!source.collection">+ Add Date Range</button>
            </div>

            <!-- Display columns (table + leaderboard mode only) -->
            <div class="field-row"
              *ngIf="draft.aggregationMode === 'client' && draft.viz === 'table' && source.collection">
              <label class="field-label">Display Columns</label>
              <p class="hint">Extra fields to show as columns in the table result.</p>
              <div class="col-picker" *ngIf="displayableFieldsFor(si).length > 0; else noColsMsg">
                <label *ngFor="let f of displayableFieldsFor(si)" class="col-check">
                  <input type="checkbox"
                    [checked]="isDisplayColumnChecked(source, f.key)"
                    (change)="toggleDisplayColumn(source, f.key)" />
                  {{ f.label }}
                </label>
              </div>
              <ng-template #noColsMsg>
                <p class="hint muted">No additional fields available.</p>
              </ng-template>
            </div>
          </div>

          <!-- Add source (multi mode) -->
          <div *ngIf="draft.mode === 'multi'" class="add-source-row">
            <button class="btn-ghost" (click)="addSource()">+ Add Source</button>
          </div>

          <!-- Visualisation -->
          <div class="card section-card">
            <label class="field-label">Visualisation</label>
            <div class="viz-toggle">
              <button *ngIf="draft.mode === 'single'" [class.active]="draft.viz === 'bar'" (click)="draft.viz = 'bar'">
                Bar Chart
              </button>
              <button *ngIf="draft.mode === 'multi'" [class.active]="draft.viz === 'multi-bar'" (click)="draft.viz = 'multi-bar'">
                Grouped Bars
              </button>
              <button [class.active]="draft.viz === 'table'" (click)="draft.viz = 'table'">
                Table
              </button>
            </div>
          </div>

          <!-- Actions -->
          <div class="actions-bar">
            <button class="btn-primary" (click)="runBuilder()" [disabled]="running">
              {{ running ? 'Running…' : 'Run Preview' }}
            </button>
            <button class="btn-secondary" (click)="saveReport()" [disabled]="running || saving">
              {{ saving ? 'Saving…' : 'Save Report' }}
            </button>
            <button *ngIf="result" class="btn-ghost" (click)="exportCsv(result!, draft.name)">Export CSV</button>
          </div>
          <div class="run-error" *ngIf="runError">{{ runError }}</div>
        </div>

        <!-- RIGHT: Preview ───────────────────────────────────────────────── -->
        <div class="preview-col">
          <div class="card preview-card">
            <div class="preview-header">
              <span>Preview</span>
              <span class="muted small" *ngIf="result">
                {{ result.rows.length }} row{{ result.rows.length !== 1 ? 's' : '' }}
              </span>
            </div>

            <!-- Loading skeleton -->
            <div class="skeleton-wrap" *ngIf="running">
              <div class="skeleton-bar" *ngFor="let n of [1,2,3,4,5]"></div>
            </div>

            <!-- Empty state -->
            <div class="preview-empty" *ngIf="!running && !result">
              Configure your report and click <strong>Run Preview</strong>.
            </div>

            <!-- Result -->
            <ng-container *ngIf="!running && result">
              <ng-container *ngTemplateOutlet="vizBlock; context: { result: result, def: draft }"></ng-container>
            </ng-container>
          </div>
        </div>
      </div>
    </div>

    <!-- ── SHARED VIZ TEMPLATE ────────────────────────────────────────── -->
    <ng-template #vizBlock let-result="result" let-def="def">

      <!-- Legend (multi mode) -->
      <div class="legend" *ngIf="def.mode === 'multi' && result.seriesLabels.length > 1">
        <span *ngFor="let lbl of result.seriesLabels; let i = index" class="legend-item">
          <i [class]="'legend-dot series-' + i"></i> {{ lbl }}
        </span>
      </div>

      <!-- Bar chart (single) -->
      <div *ngIf="def.viz === 'bar'" class="bar-chart">
        <div *ngFor="let row of result.rows" class="bar-col">
          <span class="bar-val">{{ row.values[0] | number }}</span>
          <i [style.height.%]="row.pct[0]"></i>
          <span class="bar-label">{{ row.label }}</span>
        </div>
      </div>

      <!-- Multi-bar chart -->
      <div *ngIf="def.viz === 'multi-bar'" class="bar-chart multi">
        <div *ngFor="let row of result.rows" class="bar-col-group">
          <div class="bar-series-wrap">
            <div *ngFor="let val of row.values; let i = index" class="bar-series-col">
              <span class="bar-val small">{{ val | number }}</span>
              <i [class]="'series-' + i" [style.height.%]="row.pct[i]"></i>
            </div>
          </div>
          <span class="bar-label">{{ row.label }}</span>
        </div>
      </div>

      <!-- Table -->
      <div *ngIf="def.viz === 'table'" class="table-wrap">
        <table>
          <thead>
            <tr>
              <th>{{ result.groupByLabel }}</th>
              <th *ngFor="let lbl of result.displayColumnLabels">{{ lbl }}</th>
              <th *ngFor="let lbl of result.metricLabels; let i = index">
                {{ def.mode === 'multi' ? result.seriesLabels[i] + ' — ' + lbl : lbl }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let row of result.rows">
              <td>{{ row.label }}</td>
              <td *ngFor="let val of (row.displayValues ?? [])">{{ val }}</td>
              <td *ngFor="let val of row.values">{{ val | number }}</td>
            </tr>
            <tr class="total-row" *ngIf="result.rows.length > 1">
              <td>Total</td>
              <td *ngFor="let _ of result.displayColumnLabels"></td>
              <td *ngFor="let _ of result.metricLabels; let i = index">
                {{ colTotal(result, i) | number }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

    </ng-template>
  `,
  styles: `
    :host {
      display: block;
      width: 100%;
      max-width: 1320px;
      box-sizing: border-box;
      margin: 0 auto;
    }

    .page { display: flex; flex-direction: column; gap: 1.25rem; }

    .page-header {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 1rem;
    }
    .page-header h1 {
      margin: 0 0 0.2rem;
      font-size: var(--type-page-title);
      font-weight: 700;
      color: var(--app-text);
      letter-spacing: -0.02em;
    }
    .subtitle { margin: 0; color: var(--app-muted); font-size: var(--type-body); }

    .card {
      background: var(--app-surface);
      border: 1px solid var(--app-border);
      border-radius: var(--app-radius);
      box-shadow: var(--app-shadow);
    }

    /* ── LIST TABLE ─────────────────────────────────────────────────── */
    .table-card { overflow: hidden; }
    table { width: 100%; border-collapse: collapse; }
    thead tr { background: #e6f4fb; }
    th {
      padding: 0.6rem 1rem;
      text-align: left;
      font-size: 0.78rem;
      font-weight: 700;
      color: var(--app-muted);
      text-transform: uppercase;
      letter-spacing: 0.04em;
      border-bottom: 1px solid var(--app-border);
    }
    td {
      padding: 0.7rem 1rem;
      font-size: 0.88rem;
      color: var(--app-text);
      border-bottom: 1px solid var(--app-border);
      vertical-align: middle;
    }
    tbody tr:last-child td { border-bottom: none; }
    tbody tr:hover { background: var(--app-surface-soft); }
    .report-name { font-weight: 600; }
    .muted { color: var(--app-muted); }
    .small { font-size: 0.82rem; }
    .actions-cell { display: flex; gap: 0.35rem; align-items: center; }
    .total-row td { font-weight: 700; background: var(--app-surface-soft); border-top: 2px solid var(--app-border); }

    .badge {
      display: inline-block;
      padding: 0.15rem 0.5rem;
      border-radius: 999px;
      font-size: 0.72rem;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      background: var(--app-primary-soft);
      color: var(--app-primary);
      border: 1px solid #b7dff0;
    }
    .badge.multi { background: #f0eeff; color: #6c5ce7; border-color: #c8b8ff; }

    /* ── EMPTY STATE ────────────────────────────────────────────────── */
    .empty-state {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 1rem;
      padding: 3rem 2rem;
      text-align: center;
    }
    .empty-state svg { width: 48px; height: 48px; color: var(--app-muted); opacity: 0.5; }
    .empty-state p { margin: 0; color: var(--app-muted); }

    /* ── BUTTONS ────────────────────────────────────────────────────── */
    .btn-primary {
      background: var(--app-primary);
      color: #fff;
      border: none;
      border-radius: 8px;
      padding: 0.55rem 1.1rem;
      font-size: 0.88rem;
      font-weight: 600;
      cursor: pointer;
      white-space: nowrap;
    }
    .btn-primary:hover { background: var(--app-primary-hover); }
    .btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }

    .btn-secondary {
      background: var(--app-surface-soft);
      color: var(--app-text);
      border: 1px solid var(--app-border);
      border-radius: 8px;
      padding: 0.55rem 1.1rem;
      font-size: 0.88rem;
      font-weight: 600;
      cursor: pointer;
    }
    .btn-secondary:hover { background: var(--app-border); }
    .btn-secondary:disabled { opacity: 0.6; cursor: not-allowed; }

    .btn-ghost {
      background: none;
      color: var(--app-primary);
      border: none;
      padding: 0.4rem 0.6rem;
      font-size: 0.85rem;
      cursor: pointer;
    }
    .btn-ghost:hover { text-decoration: underline; }
    .btn-ghost.small { font-size: 0.8rem; padding: 0.25rem 0.4rem; }

    .btn-sm {
      background: var(--app-surface-soft);
      color: var(--app-text);
      border: 1px solid var(--app-border);
      border-radius: 6px;
      padding: 0.3rem 0.65rem;
      font-size: 0.8rem;
      font-weight: 600;
      cursor: pointer;
    }
    .btn-sm:hover { background: #e6f4fb; }
    .btn-sm.danger { color: var(--app-danger, #e53e3e); }
    .btn-sm.danger:hover { background: #fff5f5; }

    .btn-icon {
      background: none;
      border: none;
      cursor: pointer;
      font-size: 0.8rem;
      color: var(--app-muted);
      padding: 0.2rem 0.4rem;
      border-radius: 4px;
    }
    .btn-icon:hover { background: var(--app-surface-soft); }
    .btn-icon.danger:hover { color: var(--app-danger, #e53e3e); background: #fff5f5; }

    /* ── BUILDER LAYOUT ─────────────────────────────────────────────── */
    .builder-grid {
      display: grid;
      grid-template-columns: 420px 1fr;
      gap: 1.25rem;
      align-items: start;
    }
    @media (max-width: 1000px) {
      .builder-grid { grid-template-columns: 1fr; }
    }

    .builder-col { display: flex; flex-direction: column; gap: 0.9rem; }

    .section-card { padding: 1rem 1.1rem; display: flex; flex-direction: column; gap: 0.6rem; }

    .source-card { border-left: 3px solid var(--app-primary); }

    .source-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 0.25rem;
    }
    .source-num {
      font-size: 0.78rem;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      color: var(--app-primary);
    }

    .field-label {
      font-size: 0.8rem;
      font-weight: 700;
      color: var(--app-muted);
      text-transform: uppercase;
      letter-spacing: 0.04em;
      margin-bottom: 0.15rem;
    }
    .required { color: var(--app-danger, #e53e3e); }
    .field-error { font-size: 0.8rem; color: var(--app-danger, #e53e3e); }

    .field-row { display: flex; flex-direction: column; gap: 0.35rem; }

    .text-input {
      width: 100%;
      box-sizing: border-box;
      border: 1px solid var(--app-border);
      border-radius: 8px;
      padding: 0.5rem 0.75rem;
      font-size: 0.88rem;
      color: var(--app-text);
      background: var(--app-surface);
      outline: none;
    }
    .text-input:focus { border-color: var(--app-primary); }

    .select-input {
      width: 100%;
      box-sizing: border-box;
      border: 1px solid var(--app-border);
      border-radius: 8px;
      padding: 0.5rem 0.75rem;
      font-size: 0.88rem;
      color: var(--app-text);
      background: var(--app-surface);
      outline: none;
      cursor: pointer;
    }
    .select-input:focus { border-color: var(--app-primary); }
    .select-input.short { width: auto; min-width: 90px; flex-shrink: 0; }

    .select-wrap { position: relative; }
    .loading-hint { font-size: 0.75rem; color: var(--app-muted); position: absolute; right: 8px; top: 50%; transform: translateY(-50%); pointer-events: none; }

    .metric-row { display: flex; align-items: center; gap: 0.5rem; }
    .metric-of { font-size: 0.85rem; color: var(--app-muted); flex-shrink: 0; }

    .filter-row { display: flex; align-items: center; gap: 0.4rem; flex-wrap: nowrap; }
    .filter-val { min-width: 80px; flex: 1; }

    .mode-toggle, .viz-toggle {
      display: flex;
      gap: 0;
      border: 1px solid var(--app-border);
      border-radius: 8px;
      overflow: hidden;
    }
    .mode-toggle button, .viz-toggle button {
      flex: 1;
      background: var(--app-surface);
      border: none;
      border-right: 1px solid var(--app-border);
      padding: 0.45rem 0.75rem;
      font-size: 0.83rem;
      font-weight: 500;
      color: var(--app-muted);
      cursor: pointer;
      transition: background 0.15s;
    }
    .mode-toggle button:last-child, .viz-toggle button:last-child { border-right: none; }
    .mode-toggle button.active, .viz-toggle button.active {
      background: var(--app-primary);
      color: #fff;
      font-weight: 700;
    }

    .hint { margin: 0; font-size: 0.8rem; color: var(--app-muted); }

    .leaderboard-opts { display: flex; flex-direction: column; gap: 0.75rem; margin-top: 0.25rem; }
    .lb-opt { display: flex; flex-direction: column; gap: 0.35rem; }
    .topn-row { display: flex; align-items: center; gap: 0.5rem; }
    .topn-input { width: 72px !important; text-align: center; }

    /* ── COLUMN PICKER ──────────────────────────────────────────────── */
    .col-picker {
      display: flex;
      flex-direction: column;
      gap: 0.3rem;
      max-height: 180px;
      overflow-y: auto;
      padding: 0.4rem 0.5rem;
      border: 1px solid var(--app-border);
      border-radius: 8px;
      background: var(--app-surface-soft);
    }
    .col-check {
      display: flex;
      align-items: center;
      gap: 0.4rem;
      font-size: 0.83rem;
      color: var(--app-text);
      cursor: pointer;
    }
    .col-check input[type="checkbox"] { cursor: pointer; accent-color: var(--app-primary); }

    .add-source-row { display: flex; justify-content: center; }

    .actions-bar { display: flex; gap: 0.6rem; align-items: center; flex-wrap: wrap; }
    .run-error { font-size: 0.83rem; color: var(--app-danger, #e53e3e); margin-top: 0.25rem; }

    /* ── PREVIEW ────────────────────────────────────────────────────── */
    .preview-col { position: sticky; top: 1rem; }
    .preview-card { padding: 1.1rem; min-height: 360px; display: flex; flex-direction: column; }
    .preview-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 1rem;
      font-weight: 700;
      font-size: 0.9rem;
      color: var(--app-text);
    }
    .preview-empty { flex: 1; display: flex; align-items: center; justify-content: center; color: var(--app-muted); font-size: 0.88rem; text-align: center; }

    /* ── SKELETON ───────────────────────────────────────────────────── */
    .skeleton-wrap { display: flex; align-items: flex-end; gap: 12px; padding: 1rem 0; height: 220px; }
    .skeleton-bar {
      flex: 1;
      background: linear-gradient(90deg, var(--app-surface-soft) 25%, #e0e0e0 50%, var(--app-surface-soft) 75%);
      background-size: 200% 100%;
      border-radius: 4px 4px 0 0;
      animation: shimmer 1.3s infinite;
    }
    .skeleton-bar:nth-child(1) { height: 60%; }
    .skeleton-bar:nth-child(2) { height: 85%; }
    .skeleton-bar:nth-child(3) { height: 45%; }
    .skeleton-bar:nth-child(4) { height: 70%; }
    .skeleton-bar:nth-child(5) { height: 55%; }
    @keyframes shimmer { to { background-position: -200% 0; } }

    /* ── CHARTS ─────────────────────────────────────────────────────── */
    .bar-chart {
      min-height: 240px;
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(36px, 1fr));
      align-items: end;
      gap: 8px;
      padding: 0.5rem 0;
    }
    .bar-col {
      display: grid;
      justify-items: center;
      align-items: end;
      gap: 4px;
    }
    .bar-col i {
      width: 28px;
      min-height: 3px;
      max-height: 200px;
      background: var(--jci-blue, #0097d7);
      border-radius: 4px 4px 0 0;
      display: block;
    }
    .bar-val { font-size: 0.72rem; font-weight: 700; color: var(--app-text); }
    .bar-val.small { font-size: 0.65rem; }
    .bar-label { font-size: 0.75rem; color: var(--app-muted); text-align: center; word-break: break-all; }

    /* Multi-bar */
    .bar-chart.multi {
      grid-template-columns: repeat(auto-fit, minmax(60px, 1fr));
    }
    .bar-col-group { display: flex; flex-direction: column; align-items: center; gap: 4px; }
    .bar-series-wrap { display: flex; align-items: flex-end; gap: 3px; height: 200px; }
    .bar-series-col { display: flex; flex-direction: column; align-items: center; justify-content: flex-end; gap: 2px; }
    .bar-series-col i {
      width: 18px;
      min-height: 3px;
      border-radius: 3px 3px 0 0;
      display: block;
    }

    /* Series colours */
    .series-0 { background: var(--jci-blue, #0097d7); }
    .series-1 { background: #6c5ce7; }
    .series-2 { background: #00b894; }
    .series-3 { background: #e17055; }
    .series-4 { background: #fdcb6e; }

    /* ── LEGEND ─────────────────────────────────────────────────────── */
    .legend { display: flex; gap: 1rem; flex-wrap: wrap; margin-bottom: 0.75rem; }
    .legend-item { display: flex; align-items: center; gap: 0.35rem; font-size: 0.8rem; color: var(--app-muted); }
    .legend-dot { width: 10px; height: 10px; border-radius: 2px; display: inline-block; }

    /* ── TABLE IN RESULT ────────────────────────────────────────────── */
    .table-wrap { overflow-x: auto; }

    /* ── RESULT CARD IN LIST VIEW ───────────────────────────────────── */
    .result-card { padding: 1rem; }
    .result-header {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      margin-bottom: 1rem;
    }
    .result-title { font-weight: 700; font-size: 0.95rem; color: var(--app-text); flex: 1; }
  `
})
export class ReportsComponent implements OnInit {
  view: 'list' | 'builder' = 'list';
  isEditing = false;

  savedReports: ReportDefinition[] = [];
  loadingReports = false;
  draft!: ReportDefinition;

  availableCollections: string[] = [];
  loadingCollections = false;

  readonly datePresetOptions = DATE_PRESET_OPTIONS;

  // Per-source field cache (index → fields)
  private sourceFields: ResolvedField[][] = [];

  running = false;
  saving = false;
  result: ReportResult | null = null;
  runError = '';
  nameError = '';

  // For list-view inline result
  listResult: ReportResult | null = null;
  listResultName = '';
  listResultDef: ReportDefinition | null = null;

  constructor(readonly svc: ReportService) {}

  ngOnInit(): void {
    this.draft = this.svc.freshDraft();
    this.loadReports();
    this.loadCollectionList();
  }

  private async loadReports(): Promise<void> {
    this.loadingReports = true;
    try { this.savedReports = await this.svc.list(); }
    catch { this.savedReports = []; }
    finally { this.loadingReports = false; }
  }

  private async loadCollectionList(): Promise<void> {
    this.loadingCollections = true;
    this.availableCollections = await this.svc.loadCollections();
    this.loadingCollections = false;
  }

  // ── Navigation ────────────────────────────────────────────────────────

  newReport(): void {
    this.draft = this.svc.freshDraft();
    this.sourceFields = [[]];
    this.result = null;
    this.runError = '';
    this.nameError = '';
    this.isEditing = false;
    this.view = 'builder';
  }

  editReport(report: ReportDefinition): void {
    this.draft = JSON.parse(JSON.stringify(report)) as ReportDefinition;
    this.sourceFields = this.draft.sources.map(() => []);
    this.result = null;
    this.runError = '';
    this.nameError = '';
    this.isEditing = true;
    this.view = 'builder';
    // Pre-load fields for each source
    this.draft.sources.forEach((_, i) => this.ensureFieldsLoaded(i));
  }

  backToList(): void {
    this.view = 'list';
    this.result = null;
  }

  // ── Mode / collection changes ─────────────────────────────────────────

  setMode(mode: 'single' | 'multi'): void {
    this.draft.mode = mode;
    if (mode === 'single') {
      this.draft.sources = [this.draft.sources[0] ?? this.svc.freshSource()];
      this.sourceFields = [this.sourceFields[0] ?? []];
      this.draft.viz = 'bar';
    } else {
      if (this.draft.sources.length < 2) {
        this.draft.sources.push(this.svc.freshSource());
        this.sourceFields.push([]);
      }
      this.draft.viz = 'multi-bar';
    }
    this.result = null;
  }

  onCollectionChange(si: number): void {
    const source = this.draft.sources[si];
    source.filters = [];
    source.metric = { type: 'count' };
    source.displayColumns = [];
    this.sourceFields[si] = [];
    this.ensureFieldsLoaded(si);
    this.result = null;
  }

  onGroupByChange(): void {
    this.result = null;
  }

  private async ensureFieldsLoaded(si: number): Promise<void> {
    const col = this.draft.sources[si]?.collection;
    if (!col) return;
    this.sourceFields[si] = await this.svc.loadFields(col);
  }

  // ── Source management ─────────────────────────────────────────────────

  addSource(): void {
    this.draft.sources.push(this.svc.freshSource());
    this.sourceFields.push([]);
  }

  removeSource(si: number): void {
    this.draft.sources.splice(si, 1);
    this.sourceFields.splice(si, 1);
    this.result = null;
  }

  // ── Filter helpers ────────────────────────────────────────────────────

  addFilter(source: ReportSource): void {
    source.filters.push(this.svc.freshFilter());
  }

  removeFilter(source: ReportSource, fi: number): void {
    source.filters.splice(fi, 1);
  }

  onFilterFieldChange(source: ReportSource, filter: ReportFilter): void {
    filter.operator = '_eq';
    filter.value = '';
  }

  onMetricTypeChange(source: ReportSource): void {
    if (source.metric.type === 'count') source.metric.field = undefined;
  }

  isNullOperator(op: FilterOperator): boolean {
    return op === '_null' || op === '_nnull';
  }

  // ── Date preset helpers ───────────────────────────────────────────────

  addDatePreset(source: ReportSource): void {
    const firstDatetime = (this.sourceFields[this.draft.sources.indexOf(source)] ?? [])
      .find(f => f.type === 'datetime' && !f.key.includes('.'));
    source.datePresets.push(this.svc.freshDatePreset(firstDatetime?.key ?? ''));
  }

  removeDatePreset(source: ReportSource, di: number): void {
    source.datePresets.splice(di, 1);
  }

  datetimeFieldsFor(si: number): ResolvedField[] {
    return (this.sourceFields[si] ?? []).filter(f => f.type === 'datetime' && !f.key.includes('.'));
  }

  /** All fields eligible to be shown as extra table columns (excludes M2O pointer fields). */
  displayableFieldsFor(si: number): ResolvedField[] {
    const source = this.draft.sources[si];
    return (this.sourceFields[si] ?? []).filter(f =>
      !f.relatedCollection &&                      // skip M2O pointer fields (use expanded dot-notation instead)
      f.key !== source?.metric?.field &&            // skip the metric field (already shown)
      f.key !== this.draft.groupBy                  // skip the group-by field (already the row label)
    );
  }

  isDisplayColumnChecked(source: ReportSource, key: string): boolean {
    return (source.displayColumns ?? []).includes(key);
  }

  toggleDisplayColumn(source: ReportSource, key: string): void {
    if (!source.displayColumns) source.displayColumns = [];
    const idx = source.displayColumns.indexOf(key);
    if (idx === -1) {
      source.displayColumns.push(key);
    } else {
      source.displayColumns.splice(idx, 1);
    }
    this.result = null; // invalidate preview when columns change
  }

  // ── Field selectors ───────────────────────────────────────────────────

  filterableFieldsFor(si: number): ResolvedField[] {
    return (this.sourceFields[si] ?? []).filter((f) => !f.key.includes('.') || f.relatedCollection === undefined);
  }

  numericFieldsFor(si: number): ResolvedField[] {
    return (this.sourceFields[si] ?? []).filter((f) => f.numeric);
  }

  operatorsFor(source: ReportSource, filter: ReportFilter): typeof OPERATOR_OPTIONS {
    const fields = this.sourceFields[this.draft.sources.indexOf(source)] ?? [];
    const field = fields.find((f) => f.key === filter.field);
    if (!field) return OPERATOR_OPTIONS;
    return OPERATOR_OPTIONS.filter((op) => op.forTypes.includes(field.type));
  }

  groupByOptionsFor(si: number): Array<{ value: string; label: string }> {
    const fields = this.sourceFields[si] ?? [];
    const isClient = this.draft.aggregationMode === 'client';
    const opts: Array<{ value: string; label: string }> = [];
    for (const f of fields) {
      // Server mode: only direct fields (no dot-notation) — Directus aggregation limitation
      // Client mode: all fields including relational dot-notation
      if (!isClient && (f.relatedCollection || f.key.includes('.'))) continue;
      // In client mode skip the M2O pointer itself (e.g. 'profile_id') — use the expanded fields instead
      if (isClient && f.relatedCollection) continue;
      opts.push({ value: f.key, label: f.label });
      if (!isClient && f.type === 'datetime') {
        opts.push({ value: `month(${f.key})`, label: `Month (${f.label})` });
        opts.push({ value: `year(${f.key})`, label: `Year (${f.label})` });
      }
    }
    return opts;
  }

  setAggregationMode(mode: 'server' | 'client'): void {
    this.draft.aggregationMode = mode;
    this.draft.groupBy = null; // reset groupBy as available options change
    this.result = null;
    if (mode === 'client') {
      this.draft.viz = 'bar'; // bar chart is the natural leaderboard viz
    }
  }

  // ── Run ───────────────────────────────────────────────────────────────

  async runBuilder(): Promise<void> {
    this.runError = '';
    if (!this.draft.sources.every((s) => s.collection)) {
      this.runError = 'Please select a collection for each source.';
      return;
    }
    this.running = true;
    this.result = null;
    try {
      this.result = await this.svc.run(this.draft);
      // Auto-switch viz when no groupBy (single value — table is more informative)
      if (!this.draft.groupBy && this.draft.viz !== 'table') {
        this.draft.viz = 'table';
      }
    } catch (err: unknown) {
      this.runError = (err as { message?: string })?.message ?? 'Failed to run report. Check collection permissions.';
    } finally {
      this.running = false;
    }
  }

  async runReport(report: ReportDefinition): Promise<void> {
    this.listResult = null;
    this.listResultName = report.name;
    this.listResultDef = report;
    try {
      this.listResult = await this.svc.run(report);
    } catch {
      // silently ignore — result stays null
    }
  }

  // ── Save / Delete ─────────────────────────────────────────────────────

  async saveReport(): Promise<void> {
    this.nameError = '';
    if (!this.draft.name.trim()) {
      this.nameError = 'Please enter a report name.';
      return;
    }
    this.saving = true;
    try {
      const saved = await this.svc.save(this.draft);
      this.draft = saved;
      this.savedReports = await this.svc.list();
      this.view = 'list';
    } catch {
      this.nameError = 'Failed to save. Check your connection and try again.';
    } finally {
      this.saving = false;
    }
  }

  async deleteReport(id: string): Promise<void> {
    try {
      await this.svc.delete(id);
      this.savedReports = await this.svc.list();
      if (this.listResultDef?.id === id) this.listResult = null;
    } catch { /* silently ignore */ }
  }

  // ── CSV Export ────────────────────────────────────────────────────────

  exportCsv(result: ReportResult, name: string): void {
    const headers = [result.groupByLabel, ...(result.displayColumnLabels ?? []), ...result.metricLabels];
    const body = result.rows.map((row) => [
      row.label,
      ...(row.displayValues ?? []),
      ...row.values.map(String),
    ]);
    const csv = [headers, ...body]
      .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `${(name || 'report').replace(/\s+/g, '-').toLowerCase()}.csv`;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  }

  // ── Helpers ───────────────────────────────────────────────────────────

  collectionsOf(def: ReportDefinition): string {
    return [...new Set(def.sources.map((s) => s.collection))].join(', ');
  }

  colTotal(result: ReportResult, colIdx: number): number {
    return result.rows.reduce((sum, row) => sum + (row.values[colIdx] ?? 0), 0);
  }
}
